import { useState, useCallback } from 'react';
import { Message, TimelineItem } from '@/features/messages/messages.types';
import { ExtendedMessage } from '../conversations.types';

interface UseMessageSendingProps {
  refreshMessages: () => void;
  timeline: TimelineItem[];
}

interface UseMessageSendingReturn {
  pendingMessages: Message[];
  isBackgroundRefreshing: boolean;
  handleMessageSent: (newMessage: Message) => void;
}

export const useMessageSending = ({
  refreshMessages,
  timeline,
}: UseMessageSendingProps): UseMessageSendingReturn => {
  const [pendingMessages, setPendingMessages] = useState<Message[]>([]);
  const [isBackgroundRefreshing, setIsBackgroundRefreshing] = useState(false);

  const handleMessageSent = useCallback(
    (newMessage: Message) => {
      setPendingMessages((prev: Message[]) => {
        if (newMessage._status === 'delivered') {
          if ((newMessage as ExtendedMessage)._isRetry) {
            // For retried messages that succeeded, remove the original message
            return prev.filter((msg) => msg.id !== newMessage._tempId);
          }

          // For normal delivery updates
          return prev.map((msg) => {
            if (msg.id === newMessage._tempId) {
              return {
                ...newMessage,
                id: msg.id,
                _status: 'delivered',
                _realId: newMessage.id,
              };
            }
            return msg;
          });
        } else {
          // Handle updates to pending/sent/error messages
          const existingIndex = prev.findIndex(
            (msg) =>
              msg.id === newMessage.id ||
              ((newMessage as ExtendedMessage)._isRetry &&
                msg.id === newMessage._tempId),
          );

          if (existingIndex >= 0) {
            // Update existing message
            const updated = [...prev];
            updated[existingIndex] = newMessage;
            return updated;
          } else {
            // Add new pending message
            return [...prev, newMessage];
          }
        }
      });

      // Start background refresh for pending/delivered messages
      if (
        newMessage._status === 'pending' ||
        newMessage._status === 'delivered'
      ) {
        if (!isBackgroundRefreshing) {
          setIsBackgroundRefreshing(true);

          setTimeout(() => {
            refreshMessages();
            setIsBackgroundRefreshing(false);

            // Clean up delivered messages after refresh
            if (newMessage._status === 'delivered') {
              setTimeout(() => {
                setPendingMessages((prev) => {
                  return prev.filter((msg) => {
                    // Keep messages that aren't delivered yet
                    if (msg._status !== 'delivered') return true;

                    // Special handling for attachment messages
                    if (msg.attachments?.length) {
                      const isInTimeline = timeline.some((item) => {
                        if (item.type !== 'message') return false;
                        const timelineMsg = item.content as Message;

                        // Check ID matches
                        if (
                          timelineMsg.id === msg.id ||
                          timelineMsg.id === (msg as ExtendedMessage)._realId
                        ) {
                          return true;
                        }

                        // Check attachment URL matches
                        if (timelineMsg.attachments && msg.attachments) {
                          return timelineMsg.attachments.some(
                            (attachment, i) =>
                              attachment.file_url ===
                              msg.attachments![i]?.file_url,
                          );
                        }

                        return false;
                      });

                      return !isInTimeline;
                    }

                    // For non-attachment messages, remove if delivered
                    return false;
                  });
                });
              }, 6000);
            }
          }, 300);
        }
      }
    },
    [refreshMessages, timeline, isBackgroundRefreshing],
  );

  return {
    pendingMessages,
    isBackgroundRefreshing,
    handleMessageSent,
  };
};
